import { Alert, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { PromptLanguageSelect, FALLBACK_LANGUAGES } from './PromptLanguageSelect';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CrudPage } from '../../shared/components/CrudPage';
import { GeneratePromptsDialog } from './GeneratePromptsDialog';
import { resources } from '../../core/services/resources';
import { useLineOfBiz } from '../../core/contexts/LineOfBizContext';
import { useTenant } from '../../core/contexts/TenantContext';
import { StatusChip } from '../../shared/components/StatusChip';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { TreeEntitySelect } from '../../shared/components/TreeEntitySelect';
import { LineOfBizSelect } from '../lines-of-business/LineOfBizSelect';
import type {
  LineOfBizCategory,
  LineOfBizPersona,
  Prompt,
  PromptImportance,
  PromptIntentionType,
  TenantBrand,
} from '../../core/types/data-models';

const defaultImportances: PromptImportance[] = ['LOW', 'MEDIUM', 'HIGH'];
const intentionTypes: PromptIntentionType[] = [
  'BRANDED',
  'UNBRANDED',
  'INFORMATIONAL',
  'COMPARISON',
];

export function PromptsPage() {
  const { activeTenantId } = useTenant();
  const { activeLineOfBizId } = useLineOfBiz();
  // Open-state for the GeneratePromptsDialog. Lives at page scope so
  // closing/re-opening the dialog doesn't drop pending state managed
  // inside it.
  const [generateOpen, setGenerateOpen] = useState(false);

  // Lookup queries — same cache keys as the dialog dropdowns so each
  // resource is fetched once and the table/form share it. The default
  // category/persona/brand FKs now point at the LOB-/tenant-scoped tables.
  const categories = useQuery({
    queryKey: ['line-of-biz-categories', 'select'],
    queryFn: () =>
      resources.lineOfBizCategories.list({ pageSize: 200, sort: 'name' }),
  });
  const personas = useQuery({
    queryKey: ['line-of-biz-personas', 'select'],
    queryFn: () =>
      resources.lineOfBizPersonas.list({ pageSize: 200, sort: 'name' }),
  });
  const stages = useQuery({
    queryKey: ['prompt-funnel-stages', 'select'],
    queryFn: () => resources.promptFunnelStages.list({ pageSize: 200, sort: 'name' }),
  });
  const brands = useQuery({
    queryKey: ['tenant-brands', 'select', 'all'],
    queryFn: () => resources.tenantBrands.list({ pageSize: 200, sort: 'name' }),
  });

  const nameById = (rows?: { id: number; name: string }[]) => {
    const m = new Map<number, string>();
    for (const r of rows ?? []) m.set(r.id, r.name);
    return m;
  };
  const categoryName = useMemo(() => nameById(categories.data?.data), [categories.data]);
  const personaName = useMemo(() => nameById(personas.data?.data), [personas.data]);
  const stageName = useMemo(() => nameById(stages.data?.data), [stages.data]);
  const brandName = useMemo(() => nameById(brands.data?.data), [brands.data]);

  const columns: GridColDef[] = useMemo(() => {
    const lookupCell =
      (map: Map<number, string>) =>
      (p: GridRenderCellParams) => {
        const id = p.value as number | null | undefined;
        return id == null ? '' : map.get(id) ?? `#${id}`;
      };
    return [
      { field: 'id', headerName: 'ID', width: 70 },
      { field: 'prompt', headerName: 'Prompt text', flex: 1, minWidth: 320 },
      { field: 'language', headerName: 'Lang', width: 80 },
      {
        field: 'intentionType',
        headerName: 'Intent',
        width: 140,
        renderCell: (p) => <StatusChip value={p.value as string} />,
      },
      { field: 'defaultCategoryId', headerName: 'Default Category', width: 180, renderCell: lookupCell(categoryName) },
      { field: 'defaultPersonaId', headerName: 'Default Persona', width: 180, renderCell: lookupCell(personaName) },
      { field: 'defaultStageId', headerName: 'Default Stage', width: 180, renderCell: lookupCell(stageName) },
      { field: 'defaultBrandId', headerName: 'Default Brand', width: 200, renderCell: lookupCell(brandName) },
      {
        field: 'defaultImportance',
        headerName: 'Importance',
        width: 130,
        renderCell: (p) => <StatusChip value={p.value as string} />,
      },
    ];
  }, [categoryName, personaName, stageName, brandName]);

  return (
    <>
    <CrudPage<Prompt>
      title="Prompts"
      description="Tenant- and LOB-scoped prompt catalog."
      // Sits to the left of the `+ New` button rendered by CrudPage.
      // Placeholder for now — handler is intentionally a no-op until
      // the generate-prompts LLM action is wired (same pattern the
      // Categories / Personas / Competitors pages used while the
      // backend action was being built).
      // Browse-by-default — same convention as the Categories /
      // Personas / Orgs & Brands pages. Users hit "Edit mode" to get
      // the + New / Generate / per-row edit-delete affordances.
      initialMode="view"
      headerActions={
        <Button
          variant="outlined"
          startIcon={<AutoAwesomeIcon />}
          disabled={activeLineOfBizId == null}
          // `whiteSpace: nowrap` keeps the label on a single line —
          // without it MUI Button can let "GENERATE PROMPTS" wrap when
          // the actions area is tight, which made the button taller
          // than the matching button on other pages.
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => setGenerateOpen(true)}
        >
          Generate prompts
        </Button>
      }
      // Cache key includes the active LOB so the list re-fetches when the
      // user switches LOB. The page header above renders empty data when
      // no LOB is selected — see the guard below.
      cacheKey={['prompts', { lineOfBizId: activeLineOfBizId }]}
      service={resources.prompts}
      columns={columns}
      // Only list prompts for the active LOB. The backend tenant
      // extension already filters by tenant, so we only add the
      // LOB-level filter here.
      listQuery={
        activeLineOfBizId != null ? { filter: { lineOfBizId: activeLineOfBizId } } : { filter: {} }
      }
      defaultValues={{
        prompt: '',
        lineOfBizId: activeLineOfBizId ?? undefined,
        defaultCategoryId: null,
        defaultPersonaId: null,
        defaultStageId: null,
        defaultBrandId: null,
        // PromptLanguageSelect snaps this to the selected LOB's first
        // supported language once that LOB resolves, so a plain default
        // is enough here.
        language: FALLBACK_LANGUAGES[0],
        intentionType: 'INFORMATIONAL',
        defaultImportance: 'MEDIUM',
        tenantId: activeTenantId ?? undefined,
      }}
      beforeSubmit={(v) => ({
        ...v,
        // Backstop in case the form is opened before the LOB context
        // has resolved; the user is expected to pick one via the
        // LineOfBizSelect below.
        lineOfBizId: v.lineOfBizId ?? activeLineOfBizId ?? undefined,
        tenantId: v.tenantId ?? activeTenantId ?? undefined,
      })}
      renderForm={({ values, setValues }) => (
        <Stack spacing={2}>
          {activeLineOfBizId == null && (
            <Alert severity="info">
              Pick a line of business in the sidebar — every prompt must belong to one.
            </Alert>
          )}
          <TextField
            label="Prompt text"
            required
            multiline
            minRows={3}
            value={values.prompt ?? ''}
            onChange={(e) => setValues({ ...values, prompt: e.target.value })}
            helperText="The unique identifier is derived from a hash of this text by the server."
          />
          <LineOfBizSelect
            label="Line of business"
            helperText="Required. The LOB this prompt belongs to."
            required
            value={values.lineOfBizId ?? null}
            onChange={(id) => setValues({ ...values, lineOfBizId: id ?? undefined })}
          />
          <Stack direction="row" spacing={2}>
            <PromptLanguageSelect
              lineOfBizId={values.lineOfBizId ?? activeLineOfBizId}
              value={values.language}
              onChange={(language) => setValues({ ...values, language })}
            />
            <FormControl fullWidth required>
              <InputLabel id="prompt-intention-label">Intention</InputLabel>
              <Select
                labelId="prompt-intention-label"
                label="Intention"
                value={values.intentionType ?? 'INFORMATIONAL'}
                renderValue={(v) => <StatusChip value={v as string} />}
                onChange={(e) =>
                  setValues({ ...values, intentionType: e.target.value as PromptIntentionType })
                }
              >
                {intentionTypes.map((i) => (
                  <MenuItem key={i} value={i}>
                    <StatusChip value={i} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="prompt-defaultImportance-label">Importance</InputLabel>
              <Select
                labelId="prompt-defaultImportance-label"
                label="Importance"
                value={values.defaultImportance ?? 'MEDIUM'}
                renderValue={(v) => <StatusChip value={v as string} />}
                onChange={(e) =>
                  setValues({ ...values, defaultImportance: e.target.value as PromptImportance })
                }
              >
                {defaultImportances.map((i) => (
                  <MenuItem key={i} value={i}>
                    <StatusChip value={i} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={2}>
            <EntitySelect<LineOfBizCategory>
              cacheKey={['line-of-biz-categories', 'select']}
              service={resources.lineOfBizCategories}
              label="Default category"
              value={values.defaultCategoryId}
              onChange={(id) => setValues({ ...values, defaultCategoryId: id })}
            />
            <EntitySelect<LineOfBizPersona>
              cacheKey={['line-of-biz-personas', 'select']}
              service={resources.lineOfBizPersonas}
              label="Default persona"
              value={values.defaultPersonaId}
              onChange={(id) => setValues({ ...values, defaultPersonaId: id })}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <EntitySelect
              cacheKey={['prompt-funnel-stages', 'select']}
              service={resources.promptFunnelStages}
              label="Default stage"
              value={values.defaultStageId}
              onChange={(id) => setValues({ ...values, defaultStageId: id })}
            />
            <TreeEntitySelect<TenantBrand>
              cacheKey={['tenant-brands', 'select', 'all']}
              service={resources.tenantBrands}
              parentIdField="parentBrandId"
              label="Default brand"
              value={values.defaultBrandId}
              onChange={(id) => setValues({ ...values, defaultBrandId: id })}
            />
          </Stack>
        </Stack>
      )}
    />
    <GeneratePromptsDialog
      open={generateOpen}
      lineOfBizId={activeLineOfBizId}
      onClose={() => setGenerateOpen(false)}
    />
    </>
  );
}
